import type { GasCylinder } from "@screen/Home/pages/GasShop/products";
import { formatOrderableGasTitle } from "@utils/func/gasTitle";
import { formatGasPriceRange } from "@utils/func/gasPriceRange";
import { useOrder } from "@utils/store/cart/useOrder";
import { Spinner, Stack, Text, XStack, YStack } from "tamagui";
import KioskIcon from "../KioskIcon";
import { kiosk } from "../kioskTheme";
import { CYLINDER_BOX, GAS_IMAGE, cylinderHeight } from "../icon/gasIcons";

/** 單張瓦斯商品卡（icon 圖卡右上角疊「+」；已加入購物車時左上角標示數量）。 */
const ProductCard = ({
  product,
  qty,
  lastQty,
  priceRangeText,
  onPick,
}: {
  product: GasCylinder;
  qty: number;
  /** 上次訂購此瓦斯的總桶數（>0 才標示；QR 入口專用） */
  lastQty: number;
  /** 參考售價區間顯示字串；未設定或瓦斯行隱藏金額時為 null → 不顯示 */
  priceRangeText: string | null;
  onPick: (p: GasCylinder) => void;
}) => {
  const img = GAS_IMAGE[product.kilogram];
  const isLast = lastQty > 0;
  return (
  <YStack
    // 「上次訂購」卡：極淺暖底 + 淡琥珀邊，作為安靜的輔助提示；強調交給上方浮起的標示，不互相搶戲。
    backgroundColor={isLast && qty === 0 ? "#FFFCF5" : "#FCFDFE"}
    borderRadius={16}
    borderWidth={1}
    borderColor={qty > 0 ? kiosk.blue : isLast ? "#FADFAE" : "#f0f0f8"}
    p={16}
    ai="center"
    position="relative"
    cursor="pointer"
    onPress={() => onPick(product)}
    pressStyle={{ scale: 0.95 }}
    style={{
      boxShadow: "0 10px 24px -14px rgba(48, 40, 106, 0.1)",
      width: "calc(50% - 7px)",
      // 標示跨出卡片上緣 → 必須允許溢出，否則會被卡片裁掉，變成「陷在裡面」
      overflow: "visible",
    } as any}>
    {/* 「上次訂購」標示：浮在卡片上緣（跨出邊界 + 投影 + 與卡片同色描邊做出脫離感），非嵌入卡片內。
        單行不換行：tamagui Text 預設會斷行，撐高膠囊變成方塊 → 必須 numberOfLines + nowrap。 */}
    {isLast && (
      <Stack
        position="absolute"
        t={-10}
        l="50%"
        px={10}
        py={4}
        borderRadius={999}
        ai="center"
        jc="center"
        style={{
          transform: "translateX(-50%)",
          background: "#F59E0B",
          // 描邊色＝卡片底色，讓膠囊與卡片邊界斷開，視覺上「浮」在卡片之上
          boxShadow: `0 4px 10px -3px rgba(217,119,6,.5), 0 0 0 3px ${qty > 0 ? "#FCFDFE" : "#FFFCF5"}`,
          whiteSpace: "nowrap",
          zIndex: 3,
        }}>
        <Text
          numberOfLines={1}
          color="#fff"
          fontSize={10.5}
          fontWeight="800"
          letterSpacing={0.2}
          style={{ whiteSpace: "nowrap" }}>
          ↻ 上次訂購 ×{lastQty}
        </Text>
      </Stack>
    )}
    {/* 已加入購物車：左上角數量徽章（對齊訂購頁的 totalQty 標示） */}
    {qty > 0 && (
      <Stack
        position="absolute"
        t={10}
        l={10}
        minWidth={22}
        height={22}
        borderRadius={11}
        px={6}
        ai="center"
        jc="center"
        backgroundColor={kiosk.danger}
        style={{ zIndex: 2 }}>
        <Text color="#fff" fontSize={11} fontWeight="800">
          {qty}
        </Text>
      </Stack>
    )}
    {/* 卡片右上角的「+」加購鈕 */}
    <Stack
      className="kiosk-grad"
      position="absolute"
      t={10}
      r={10}
      width={30}
      height={30}
      borderRadius={10}
      ai="center"
      jc="center"
      style={{ boxShadow: "0 2px 6px -2px rgba(29,111,255,.35)", zIndex: 2 }}>
      <KioskIcon name="plus" size={17} color="#fff" strokeWidth={2.4} />
    </Stack>
    {/* 瓦斯桶照片。容器固定高、內容靠下（jc="flex-end"）：所有卡片共用同一條地面線，
        大小差異才讀得出來——各自置中的話差異會被上下留白吃掉一半。 */}
    <Stack width={CYLINDER_BOX} height={CYLINDER_BOX} ai="center" jc="flex-end" mb={10} mt={4}>
      {img ? (
        <img
          src={img}
          alt={`${product.kilogram}KG`}
          style={{ height: cylinderHeight(product.kilogram, CYLINDER_BOX), width: "auto", maxWidth: "100%", objectFit: "contain", display: "block" }}
        />
      ) : (
        <Stack
          width={cylinderHeight(product.kilogram, CYLINDER_BOX)}
          height={cylinderHeight(product.kilogram, CYLINDER_BOX)}
          borderRadius={6}
          ai="center"
          jc="center"
          style={{ background: "linear-gradient(135deg,#eaf1ff,#d4e3ff)" }}>
          <KioskIcon name="cylinder" size={Math.round(cylinderHeight(product.kilogram, CYLINDER_BOX) * 0.55)} color={kiosk.blue} />
        </Stack>
      )}
    </Stack>
    {/* 對齊訂購頁：瓦斯統一顯示「{公斤數}KG 瓦斯」；商品瀏覽層不顯示實際單價，只顯示參考售價區間 */}
    <Text fontSize={15} fontWeight="700" color={kiosk.ink} textAlign="center">
      {formatOrderableGasTitle(product.kilogram)}
    </Text>
    {/* 參考售價區間（顯示用，實際金額以瓦斯行報價為準） */}
    {priceRangeText && (
      <Text fontSize={12} color={kiosk.grey} textAlign="center" mt={2}>
        參考售價 {priceRangeText}
      </Text>
    )}
  </YStack>
  );
};

const Hint = ({ children }: { children: React.ReactNode }) => (
  <YStack py={40} ai="center">
    {children}
  </YStack>
);

/** 公休但仍可預約其他日期時，在商品區上方提示客戶。 */
const ClosedReservableBanner = () => (
  <XStack
    ai="center"
    gap={9}
    mb={14}
    px={14}
    py={12}
    borderRadius={12}
    backgroundColor="#FFF7E6"
    borderWidth={1}
    borderColor="#FFD591">
    <KioskIcon name="clock" size={18} color="#D48806" />
    <Text fontSize={13} fontWeight="700" color="#AD6800" flex={1}>
      今日公休，可預約其他日期配送
    </Text>
  </XStack>
);

/** 商品網格：含載入中 / 未營業 / 無商品 / 2 欄商品卡。 */
const ProductGrid = ({
  loaded,
  canOrder,
  closedButReservable,
  products,
  lastOrderQtyByGasId,
  onPick,
}: {
  loaded: boolean;
  /** 可否下單：營業中，或公休但仍有可預約時段 */
  canOrder: boolean;
  /** 公休但仍可預約其他時段 → 顯示提示橫幅 */
  closedButReservable: boolean;
  products: GasCylinder[];
  /** 上次訂購的瓦斯數量（gas_id → 總桶數）；僅 QR 入口帶入，一般 kiosk 不傳 → 不標示 */
  lastOrderQtyByGasId?: Map<number, number>;
  onPick: (p: GasCylinder) => void;
}) => {
  // 購物車內各瓦斯品項數量（依 gas_id 加總，與訂購頁 totalQty 一致）；同一瓦斯不同安裝設定共用一張卡。
  const { productList } = useOrder();
  const qtyByGasId = productList.reduce<Map<number, number>>((map, p) => {
    if (p.gas_id == null) return map;
    const key = Number(p.gas_id);
    map.set(key, (map.get(key) ?? 0) + p.qty);
    return map;
  }, new Map());

  // 本頁是否有任一張要標「上次訂購」（決定要不要為浮起的標示預留上緣空間）
  const hasLastBadge = products.some((p) => (lastOrderQtyByGasId?.get(Number(p.gas_id)) ?? 0) > 0);

  if (!loaded) {
    return (
      <Hint>
        <Spinner size="large" color={kiosk.blue} />
      </Hint>
    );
  }
  if (!canOrder) {
    return (
      <Hint>
        <Text fontSize={18} fontWeight="bold" color={kiosk.grey}>
          尚未營業
        </Text>
      </Hint>
    );
  }
  if (products.length === 0) {
    return (
      <Hint>
        <Text fontSize={16} color={kiosk.grey}>
          尚無商品
        </Text>
      </Hint>
    );
  }
  return (
    <YStack>
      {closedButReservable && <ClosedReservableBanner />}
      {/* 有「上次訂購」標示時補上緣空間：標示會跨出卡片上緣 10px，否則第一列卡片的標示會撞到區塊標題 */}
      <XStack flexWrap="wrap" gap={14} pt={hasLastBadge ? 12 : 0}>
        {products.map((p) => (
          <ProductCard
            key={p.gas_id}
            product={p}
            qty={qtyByGasId.get(Number(p.gas_id)) ?? 0}
            lastQty={lastOrderQtyByGasId?.get(Number(p.gas_id)) ?? 0}
            priceRangeText={formatGasPriceRange(p.price_range)}
            onPick={onPick}
          />
        ))}
      </XStack>
    </YStack>
  );
};

export default ProductGrid;
